import { aiChatsReducer } from "@intelligems/ig-plugin/builder/workflows/ai-chats-reducer";
import { createInitialAiChatState } from "@intelligems/ig-plugin/builder/workflows/initial-state";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import {
  AiChatState,
  isRunningChatStatus,
} from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { ModKeys } from "@intelligems/ig-types/fe-only/personalizations";
import { TabEnumFe } from "@intelligems/ig-types/fe-only/experiences";
import { ToolResultOutput } from "@intelligems/ig-types/schemas/ai/nodes/tool-execution-result";
import { UiActionToolResult } from "@intelligems/ig-types/schemas/ai/ui-actions/union";
import { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import { createAction, createReducer } from "@reduxjs/toolkit";
import { customEventsQueryOptions } from "@src/api/custom-events/get-all-custom-events";
import { queryClient } from "@src/queryClient";
import type { AppListenerMiddleware } from "@src/store/rootState";
import {
  clearAudience,
  initAudiences,
  setAudience,
  setCurrencyTargeting,
} from "@src/store/slices/audiences";
import { resetAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { updateAnalyticsProducts } from "@src/store/slices/analyticsProducts/thunks/updateAnalyticsProducts";
import { mergeCheckoutBlock } from "@src/store/slices/checkoutBlocks";
import { activateMod } from "@src/store/slices/checkoutBlocks/thunks/activateMod";
import { replaceCheckoutBlockDisplayConditions } from "@src/store/slices/checkoutBlocks/thunks/replaceCheckoutBlockDisplayConditions";
import { setShippingProgressBarCurrency } from "@src/store/slices/checkoutBlocks/thunks/setShippingProgressBarCurrency";
import { applyAiUpsellProductRules } from "@src/store/slices/upsellProductRules/thunks/applyAiUpsellProductRules";
import { openDrawer } from "@src/store/slices/drawers";
import { setAiExclusionGroup } from "@src/store/slices/exclusionGroups";
import {
  setCustomMetrics,
  setKeyMetrics,
  setPrimaryStandardMetric,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import {
  setExperienceAnalyticsViewType,
  setExperienceDetails,
  setExperienceMode,
  setExperienceTab,
  setLinkBaseUrl,
  switchContentTestType,
  toggleRequiresLink,
} from "@src/store/slices/experiences";
import {
  updateExperienceProductTargeting,
  updateProductTargetingType,
} from "@src/store/slices/experienceProductTargeting";
import {
  addVariationMod,
  setBatchModsActivated,
  setModActivated,
} from "@src/store/slices/experienceInfoState";
import { setVariationOfferProducts } from "@src/store/slices/offerProducts";
import { setVariationOffer } from "@src/store/slices/offers";
import { resetOnsiteEditsByVariationId } from "@src/store/slices/onsiteEdits";
import {
  initOnsiteInjections,
  resetOnsiteInjections,
  resetOnsiteInjectionsByVariationId,
} from "@src/store/slices/onsiteInjections";
import {
  resetPageTargeting,
  updatePageTargeting,
} from "@src/store/slices/pageTargeting";
import {
  initRedirectsState,
  resetRedirects,
  resetRedirectsByVariationId,
  setAiTemplateRedirects,
  setAiThemeRedirects,
} from "@src/store/slices/redirects";
import {
  reshapeVariationsForMode,
  setVariations,
} from "@src/store/slices/variations";

export const clearAiChat = createAction("aiChat/clearAiChat");

export const handleAiChatAction = createAction<
  UiStreamAction | UiStreamAction[]
>("aiChat/handleAiChatAction");

export const restoreChatState = createAction<AiChatState>(
  "aiChat/restoreChatState"
);

/**
 * The send this page made for the running turn is gone (its connection died
 * before the turn settled), so nothing will ever settle the turn here. See
 * `AiChatState.interrupted`.
 */
export const markChatInterrupted = createAction("aiChat/markChatInterrupted");

export const aiChatReducer = createReducer(
  createInitialAiChatState(),
  (builder) => {
    builder
      .addCase(clearAiChat, (draft) => {
        Object.assign(draft, createInitialAiChatState());
        delete draft.interrupted;
      })
      .addCase(handleAiChatAction, (draft, action) => {
        const payload = Array.isArray(action.payload)
          ? action.payload
          : [action.payload];
        for (const item of payload) {
          aiChatsReducer(draft as AiChatState, item);
        }
        // A stream action means this page is attached to the session again —
        // a new send, or a resumed turn.
        delete draft.interrupted;
      })
      .addCase(restoreChatState, (draft, action) => {
        Object.assign(draft, action.payload);
        // A restored transcript has no request of this page attached, so a
        // turn it saved as running is one nothing here will settle.
        if (isRunningChatStatus(action.payload.status)) {
          draft.interrupted = true;
        } else {
          delete draft.interrupted;
        }
      })
      .addCase(markChatInterrupted, (draft) => {
        if (draft.sessionId) {
          draft.interrupted = true;
        }
      });
  }
);

export const getAiChat = (state: { aiChat: AiChatState }): AiChatState =>
  state.aiChat;

export const getAiChatSessionId = (state: { aiChat: AiChatState }): string =>
  state.aiChat.sessionId;

// True before the first user turn has been committed to the conversation.
// `turns` stays `[]` after `initialize` and only becomes non-empty when
// `addMessage` runs, so this survives any pre-dispatch of `initialize`
// by callers (e.g. `ExecutionPanel`) that would otherwise pre-populate
// `sessionId` and break a `!sessionId` check.
export const getIsNewSession = (state: { aiChat: AiChatState }): boolean =>
  state.aiChat.turns.length === 0;

/**
 * Session id of the Ellie (experience-creation agent) conversation the
 * merchant has actually chatted in, else `undefined`. Keyed on the session's
 * `workflowId` rather than the panel `mode`: Viv runs in this same slice, and
 * saves from inside the visual builder must not be attributed to Ellie.
 */
export const getEllieChatId = (state: {
  aiChat: AiChatState;
}): string | undefined =>
  state.aiChat.workflowId === WORKFLOW_IDS.EXPERIENCE_CREATION_SINGLE_AGENT &&
  !getIsNewSession(state) &&
  state.aiChat.sessionId
    ? state.aiChat.sessionId
    : undefined;

const isResultsData = (obj: unknown): obj is { results: unknown } => {
  if (typeof obj === "object" && obj !== null && "results" in obj) {
    return true;
  }
  return false;
};

// The custom-events query cache holds `{ customEvents: [...] }`, not a bare
// array -- unwrap it here so every ui-action case passes the reducers a real
// array instead of the wrapper object. Fetched (not read from cache): the
// merchant may never have opened a custom-events drawer/filter this session,
// so the cache can be empty or missing an event the AI just picked from
// list_custom_events -- reading a possibly-cold cache silently drops the
// selection (the reducers skip IDs they can't resolve to an event).
const getAllCustomEvents = async () => {
  const data = await queryClient.fetchQuery(customEventsQueryOptions);
  return data.customEvents;
};

const isUiAction = (obj: unknown): obj is UiActionToolResult => {
  if (
    typeof obj === "object" &&
    obj !== null &&
    "type" in obj &&
    obj.type === "ui-action"
  ) {
    return true;
  }
  return false;
};

export const setAiChatListener = (middleware: AppListenerMiddleware) => {
  middleware.startListening({
    actionCreator: handleAiChatAction,
    effect: async (action, { dispatch, getState }) => {
      let actions: UiStreamAction[] = [];
      if (Array.isArray(action.payload)) {
        actions = action.payload;
      } else {
        actions = [action.payload];
      }

      for (const streamAction of actions) {
        // Dev-only: log AI tool usage to the browser console so we can verify
        // what tools the AI actually called when something looks off in the chat.
        // This is invisible to merchants — they only see the AI's text replies.
        if (streamAction.type === "addStreamPart") {
          const part = streamAction.streamPart;
          if (part.type === "tool-input-available") {
            // eslint-disable-next-line no-console
            console.log(
              `[AI tool] ${part.toolName}`,
              part.input,
              `(callId=${part.toolCallId})`
            );
          } else if (part.type === "tool-output-available") {
            // eslint-disable-next-line no-console
            console.log(
              `[AI tool] ← output (callId=${part.toolCallId})`,
              part.output
            );
          }
        }

        if (
          streamAction.type === "addStreamPart" &&
          streamAction.streamPart.type === "tool-output-available"
        ) {
          // A tool result legitimately arrives with no `output` at all. What
          // the client gets is opt-in per tool, and the default sends
          // `{ toolCallId, toolName }` and no `output` key. The result is still
          // persisted and still goes to the model; it is just not sent here,
          // because it is often large and nothing in this listener draws it.
          //
          // This loop processes a whole batch, so reading an absent output
          // threw and abandoned every action after it in the same payload,
          // including ui-actions that put things on screen. A tool that says
          // nothing to the client is nothing to do, not a dropped batch.
          const message = streamAction.streamPart.output as
            | ToolResultOutput
            | undefined;
          const value = message?.value;

          if (!isResultsData(value) || !isUiAction(value.results)) {
            continue;
          }

          const results = value.results;

          // Process UI actions
          switch (results.action) {
            case "setExperienceDetails":
              dispatch(
                setExperienceDetails({
                  name: results.experienceDetails.name,
                  description:
                    results.experienceDetails.description || undefined,
                })
              );
              break;
            case "setVariations": {
              // Control deletion is guarded in the `setVariations` reducer
              // itself (structural invariant, needs no cross-slice data).
              // Only the started-experience check needs to live here — it
              // needs `experience.startedAtTs`, which the reducer can't see.
              const currentVariations = getState().variations.variations;
              const startedAtTs = getState().experience.experience?.startedAtTs;

              const deletedVariation = currentVariations.find(
                (v) => !results.variations.some((fv) => fv.id === v.id)
              );

              if (startedAtTs != null && deletedVariation) {
                break;
              }

              dispatch(setExperienceTab(TabEnumFe.TEST_GROUPS));
              dispatch(setVariations(results.variations));
              break;
            }
            case "setUrlRedirects":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(
                initRedirectsState({
                  redirects: results.redirects,
                  domain: results.domain,
                })
              );
              break;
            case "setTimerBlock":
            case "setTrustBadgeBlock":
            case "setUpsellBlock":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(
                mergeCheckoutBlock({
                  checkoutBlock: results.block,
                  patch: results.patch,
                })
              );
              void dispatch(
                activateMod({
                  mod: "checkoutBlock",
                  variationId: results.block.variationId,
                })
              );
              break;
            case "setShippingProgressBarBlock":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(
                mergeCheckoutBlock({
                  checkoutBlock: results.block,
                  patch: results.patch,
                })
              );
              void dispatch(
                activateMod({
                  mod: "checkoutBlock",
                  variationId: results.block.variationId,
                })
              );
              void dispatch(
                setShippingProgressBarCurrency({
                  code: results.block.variables.currency,
                })
              );
              break;
            case "setCheckoutBlockStyles":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(
                mergeCheckoutBlock({
                  checkoutBlock: results.block,
                  patch: results.patch,
                })
              );
              break;
            case "setCheckoutBlockDisplayConditions":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              void dispatch(
                replaceCheckoutBlockDisplayConditions({
                  checkoutBlockId: results.block.id,
                  displayConditions: results.block.displayConditions,
                })
              );
              break;
            case "setUpsellProductRules":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              void dispatch(
                applyAiUpsellProductRules({
                  checkoutBlockId: results.checkoutBlockId,
                  rules: results.rules,
                  productsToLoad: results.productsToLoad,
                })
              );
              break;
            case "setAudience":
              dispatch(setExperienceTab(TabEnumFe.TARGETING));
              dispatch(setAudience(results.audience as any));
              break;
            case "setCurrencyTargeting":
              dispatch(setExperienceTab(TabEnumFe.TARGETING));
              dispatch(setCurrencyTargeting(results.excludeCurrency));
              break;
            case "setLinkTargeting":
              dispatch(setExperienceTab(TabEnumFe.TARGETING));
              if (results.requiresLink) {
                dispatch(clearAudience());
              }
              dispatch(
                toggleRequiresLink({ requiresLink: results.requiresLink })
              );
              dispatch(
                setLinkBaseUrl({ linkBaseUrl: results.linkBaseUrl || "" })
              );
              break;
            case "setKeyMetrics": {
              dispatch(setExperienceTab(TabEnumFe.CONFIGURE_ANALYTICS));
              dispatch(
                setKeyMetrics({
                  standardEventList: results.standardEventList,
                  selectedCustomEventIds: results.selectedCustomEventIds,
                  allCustomEvents: await getAllCustomEvents(),
                })
              );
              dispatch(
                setPrimaryStandardMetric({
                  standardEventId: results.primaryMetric,
                })
              );
              break;
            }
            case "setCustomMetric": {
              dispatch(setExperienceTab(TabEnumFe.CONFIGURE_ANALYTICS));
              if (results.selectedCustomEventIds.length) {
                dispatch(
                  setCustomMetrics({
                    selectedCustomEventIds: results.selectedCustomEventIds,
                    allCustomEvents: await getAllCustomEvents(),
                  })
                );
              }
              if (results.openCustomEventsDrawer) {
                dispatch(openDrawer({ type: "customEvents", isCreate: true }));
              }
              break;
            }
            case "setTemplateRedirects":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(
                setAiTemplateRedirects({
                  redirects: results.redirects,
                  domain: results.domain,
                })
              );
              break;
            case "setThemeRedirects":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(
                setAiThemeRedirects({
                  redirects: results.redirects,
                  domain: results.domain,
                })
              );
              break;
            case "switchTestType":
              // Flip the experience's content type and clear the previous
              // type's residue (redirects, CSS/JS injections, and page
              // targeting) so the agent can rebuild cleanly with the matching
              // tool. Variations, details, audience, and metrics survive.
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(switchContentTestType({ testType: results.testType }));
              dispatch(resetRedirects());
              dispatch(resetOnsiteInjections());
              dispatch(resetPageTargeting());
              break;
            case "setExperienceMode": {
              // Only ever allowed on a net-new experience that has never been
              // saved. Converting reshapes variations and clears audience
              // targeting, which rewrites what an experience's variations mean
              // — and, once it has run, what its recorded exposures and
              // conversions were bucketed into. `experience.id` is assigned by
              // the server on first save, so its absence is the durable
              // "never saved" signal. (Deliberately NOT `actionMode`, which is
              // a UI-flow flag and still reads "create" after a mid-flow save.)
              //
              // The guard lives here rather than in the `setExperienceMode`
              // reducer because this case owns the whole cross-slice sequence
              // — a reducer-level check would still let the `initVariations` /
              // `initAudiences` dispatches below run.
              if (getState().experience.experience?.id) {
                break;
              }
              // Flip the experience between an A/B test and a personalization
              // (the `category` axis). The reducer sets category/type/testTypes;
              // variations and audience must be reshaped here because they live
              // in their own slices. A test gets a control + variant split; a
              // personalization gets a single 100% treatment. Audience is
              // cleared either way so a stale action (randomVariation vs
              // experienceInclude) can't linger — the agent rebuilds it with the
              // action the new mode requires.
              const isPersonalization = results.mode === "personalization";
              dispatch(
                setExperienceTab(
                  isPersonalization
                    ? TabEnumFe.TARGETING
                    : TabEnumFe.TEST_GROUPS
                )
              );
              // The reducer can't see the redirects/onsite slices itself, so
              // resolve what content actually exists here (we have
              // `getState()`) and hand it in. Needed in BOTH directions:
              // converting to a test infers the `content/*` sub-type from it
              // (theme > url > template > onsite, matching
              // `ContentExperienceBody`'s own priority for which section a
              // redirect belongs to), and converting to a personalization
              // uses it to keep the `hasTestContent*` flags that still have
              // content — those flags are all a personalization has to drive
              // `activatedModArray`.
              const existingMods = (() => {
                const currentState = getState();
                const redirectsList = currentState.redirects.redirects;
                return {
                  hasThemeMods: redirectsList.some((r) => r.isThemeTest),
                  hasUrlMods: redirectsList.some(
                    (r) => !r.isThemeTest && !r.isTemplateTest
                  ),
                  hasTemplateMods: redirectsList.some((r) => r.isTemplateTest),
                  hasOnsiteMods:
                    currentState.onsiteEdits.allEdits.length > 0 ||
                    currentState.onsiteInjections.injections.length > 0,
                  hasOffers: currentState.offers.offers.length > 0,
                };
              })();
              dispatch(setExperienceMode({ mode: results.mode, existingMods }));

              if (isPersonalization) {
                // Activate the mod sections for the content that survived.
                // The Modifications pane renders sections off `activatedModArr`
                // (empty => "No modifications added"), and on a never-saved
                // draft nothing else fills it: the route's
                // `useAndSetExperienceInfoStateFromState` derives it from the
                // FETCHED experience DTO, which doesn't exist until the
                // experience has been saved — and mode switching is restricted
                // to never-saved experiences. The create-card flow sets this
                // explicitly via `modToActivate` for the same reason.
                //
                // Only on the personalization side: the experiment body decides
                // section visibility from `actionMode`/content length instead,
                // so it needs nothing here.
                const currentState = getState();
                const modsToActivate: ModKeys[] = [];
                if (existingMods.hasThemeMods) {
                  modsToActivate.push("theme");
                }
                if (existingMods.hasTemplateMods) {
                  modsToActivate.push("template");
                }
                if (existingMods.hasUrlMods) {
                  modsToActivate.push("splitUrl");
                }
                if (currentState.onsiteEdits.allEdits.length > 0) {
                  modsToActivate.push("onsiteEdits");
                }
                if (currentState.onsiteInjections.injections.length > 0) {
                  modsToActivate.push("onsiteInjections");
                }

                dispatch(setBatchModsActivated({ mods: modsToActivate }));
              }
              // If the agent declared what this test's content actually is
              // (e.g. it was already built as a theme personalization),
              // that's more authoritative than the inference above — it
              // overrides it. `switchContentTestType` only sets
              // type/testTypes, it doesn't touch redirects/injections/
              // targeting like the destructive `switchTestType` UI action
              // does, so the content just carried over stays intact.
              // ...but NOT on an offer experience. `testType` only names
              // CONTENT sub-types (`SwitchableContentTestTypeList` has no offer
              // member), so an agent labelling an offer test reaches for
              // "content/advanced" as the closest thing it can say — and this
              // dispatch would then overwrite the `personalization/offer` type
              // set just above, sending the merchant to the content body.
              // Read the type back from state rather than re-deriving it: the
              // reducer has already decided whether this is an offer.
              const isOfferExperience =
                getState().experience.experience?.type ===
                "personalization/offer";
              if (
                !isPersonalization &&
                results.testType &&
                !isOfferExperience
              ) {
                dispatch(switchContentTestType({ testType: results.testType }));
              }
              // Reshape the EXISTING variations rather than rebuilding them.
              // Every variation-scoped slice (redirects / onsiteEdits /
              // onsiteInjections / offers) keys its rows by `variationId`, so
              // minting fresh ids here would leave all of that content orphaned
              // — still in state, unreachable from every variation-scoped
              // selector.
              //
              // A collapse (test -> personalization) legitimately drops
              // variations, and their ids do die. Clearing their content is the
              // caller's job: `reshapeVariationsForMode` can only see its own
              // slice. The invariant is that a variation id never disappears
              // while rows still reference it.
              const idsBefore = getState().variations.variations.map(
                (variation) => variation.id
              );
              dispatch(reshapeVariationsForMode({ isPersonalization }));
              const survivingIds = new Set(
                getState().variations.variations.map(
                  (variation) => variation.id
                )
              );

              for (const droppedId of idsBefore.filter(
                (id) => !survivingIds.has(id)
              )) {
                dispatch(
                  resetRedirectsByVariationId({ variationId: droppedId })
                );
                dispatch(
                  resetOnsiteEditsByVariationId({ variationId: droppedId })
                );
                dispatch(
                  resetOnsiteInjectionsByVariationId({ variationId: droppedId })
                );
              }

              dispatch(initAudiences({ audience: undefined }));
              break;
            }
            case "setOffer":
              // Offers are variation-scoped (`OfferDto.variationId`), so this
              // is a per-variation write, not a whole-experience one: a test
              // gets one call per variant with the control left without an
              // offer. The Modifications pane renders a populated offer off
              // `offers.length > 0` alone (see `PersonalizationExperience.body`),
              // so unlike the mode-switch path this needs no separate mod
              // activation.
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(
                setVariationOffer({
                  variationId: results.variationId,
                  offer: {
                    ...results.offer,
                    // The agent can't set a BXGY offer's get side yet, so an
                    // edit keeps the one the offer already has.
                    bxgyGetProductScope:
                      getState().offers.offers.find(
                        (o) => o.variationId === results.variationId
                      )?.bxgyGetProductScope ?? null,
                  },
                })
              );
              // The offer section itself renders off `offers.length > 0`, but
              // `NoActiveModificationsView` is a SIBLING of the section list
              // gated on `activatedModArr.length === 0` — so writing the offer
              // alone renders the configured discount AND an empty state
              // underneath it, with the "Add modification" button (gated on
              // `length > 0`) missing. Sections key off the discount type
              // (`PersonalizationExperience.body`), which is also a `ModKeys`
              // value, so activate that.
              //
              if (results.products) {
                // The offer's OWN eligible-product list (`offerProducts`), not
                // product targeting — `setProductTargeting` stays forbidden on
                // an offer experience. Absent means the discount applies to
                // everything, so an omitted list must not clear an existing one.
                // The agent only lists products bought, so the get set stays.
                dispatch(
                  setVariationOfferProducts({
                    variationId: results.variationId,
                    set: "buy",
                    products: results.products,
                  })
                );
              }
              // `setModActivated` TOGGLES, so a second setOffer for the same
              // variation would switch it back off — guard on current state
              // rather than dispatching blind.
              {
                const modKey = results.offer.discountApplicationType as ModKeys;

                if (modKey) {
                  // Two different maps, read by two different bodies, so both
                  // have to be set:
                  //  - a personalization renders off the GLOBAL
                  //    `activatedModArr` (`PersonalizationExperience.body`)
                  //  - an offer TEST renders off the PER-VARIATION
                  //    `activatedVariationMods`
                  //    (`InnerOfferPersonalizationMods`)
                  // Setting only one leaves the offer correct in Redux but
                  // invisible in whichever body reads the other.
                  //
                  // `setModActivated` TOGGLES, so a second setOffer for the
                  // same variation would switch it back off — guard on current
                  // state. `addVariationMod` already no-ops on a duplicate.
                  const activated =
                    getState().experienceInfoState.activatedModArr;
                  if (!activated.includes(modKey)) {
                    dispatch(setModActivated({ key: modKey }));
                  }
                  dispatch(
                    addVariationMod({
                      variationId: results.variationId,
                      mod: modKey,
                    })
                  );
                }
              }
              break;
            case "setOnsiteInjections":
              dispatch(setExperienceTab(TabEnumFe.MODS));
              dispatch(initOnsiteInjections(results.injections));
              break;
            case "setExclusionGroup":
              dispatch(setExperienceTab(TabEnumFe.TARGETING));
              dispatch(setAiExclusionGroup(results.exclusionGroup));
              break;
            case "setPageTargeting":
              dispatch(setExperienceTab(TabEnumFe.TARGETING));
              dispatch(
                updatePageTargeting({
                  experienceId: results.pageTargeting.experienceId,
                  expressionsList: (results.pageTargeting.expression ||
                    []) as any[],
                })
              );
              break;
            case "setProductTargeting":
              dispatch(setExperienceTab(TabEnumFe.TARGETING));
              // Write the expression first — it creates the targeting entry
              // (via initial state) that `updateProductTargetingType` then
              // stamps the chosen surface (productPage/productCard) onto.
              dispatch(
                updateExperienceProductTargeting({
                  experienceId: results.productTargeting.experienceId,
                  expressionsList: (results.productTargeting.expression ||
                    []) as any[],
                })
              );
              dispatch(
                updateProductTargetingType({
                  value: results.productTargeting.target,
                })
              );
              break;
            case "setAnalyticsViewType":
              dispatch(setExperienceTab(TabEnumFe.CONFIGURE_ANALYTICS));
              dispatch(
                setExperienceAnalyticsViewType(results.analyticsViewType)
              );
              // Only the products-scoped mode carries products; going back to
              // all-orders clears any prior selection.
              if (results.analyticsViewType === "test_orders_only") {
                void dispatch(updateAnalyticsProducts(results.productIds));
              } else {
                dispatch(resetAnalyticsProducts());
              }
              break;
            case "handoffToVisualEditor":
              // Dynamic import, not a static one: `experienceBuilder`'s thunk
              // tree is a large, feature-level subtree with its own internal
              // circular references (mod handlers, mixpanel tracking) that
              // only stay safe under a specific module-evaluation order.
              // `aiChat` is small and loads early; giving it a static edge
              // into that subtree changes when those modules first evaluate
              // and has already tipped over two unrelated pre-existing
              // cycles (modHandlerRegistry <-> imageModHandler,
              // getVisualBuilderTrackingContext <-> aiChat's own selectors).
              // A dynamic import keeps this slice's own module graph free of
              // that dependency; the chunk loads only when the tool actually
              // fires. Reuses the exact same thunk chain as the manual "Open
              // visual builder" button in ExperienceCreationSingleAgentChat:
              // snapshot the still-open transcript as Viv's hand-off brief,
              // close this chat, open the builder — see
              // handoffCreationToBuilder's own doc comment for why order
              // matters there.
              void import("@src/store/slices/experienceBuilder/ai/thunks/handoffCreationToBuilder").then(
                ({ handoffCreationToBuilder }) =>
                  dispatch(
                    handoffCreationToBuilder({
                      initialComponentType: results.initialComponentType,
                    })
                  )
              );
              break;
            case "handoffToExperienceCreation":
              // The reverse direction — same dynamic-import reasoning as
              // handoffToVisualEditor above. handoffBuilderToCreation snapshots
              // Viv's still-open transcript as the creation chat's hand-off
              // brief, closes the builder (reusing its own unsaved-changes
              // confirmation), and opens the creation chat.
              void import("@src/store/slices/experienceBuilder/ai/thunks/handoffBuilderToCreation").then(
                ({ handoffBuilderToCreation }) =>
                  dispatch(handoffBuilderToCreation())
              );
              break;
            default:
          }
        }
      }
    },
  });
};
